import { useState, useEffect } from 'react';

/**
 * Hook to detect user's reduced motion preference
 * Returns true if user prefers reduced motion
 */
export function useReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check if we're in a browser environment and matchMedia is supported
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    // Set initial value
    setPrefersReducedMotion(mediaQuery.matches);

    // Listen for changes
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    // Cleanup
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return prefersReducedMotion;
}

/**
 * Hook that returns appropriate animation duration based on user preference
 * @param normalDuration - Normal animation duration in ms
 * @param reducedDuration - Reduced animation duration in ms (default: 0)
 */
export function useAnimationDuration(
  normalDuration: number, 
  reducedDuration: number = 0
): number {
  const prefersReducedMotion = useReducedMotion();
  return prefersReducedMotion ? reducedDuration : normalDuration;
}

/**
 * Hook that returns CSS transition values respecting reduced motion
 * @param transition - Normal CSS transition string
 * @param reducedTransition - Reduced CSS transition string (default: 'none')
 */
export function useTransition(
  transition: string, 
  reducedTransition: string = 'none'
): string {
  const prefersReducedMotion = useReducedMotion();
  return prefersReducedMotion ? reducedTransition : transition;
}